import { ActionIcon, Loader, Menu, Text, Tooltip, UnstyledButton } from "@mantine/core";
import {
  Archive,
  Copy,
  EllipsisVertical,
  LayoutGrid,
  PanelLeftClose,
  PanelLeftOpen,
  SquareStack,
  Trash,
  X,
} from "lucide-react";
import { useState } from "react";

import type { AdCopy } from "../../../api/generated/model";
import { ProductListPopover } from "../../../platform/media/ProductListPopover";
import { SubmitSearch } from "../../../ui/SubmitSearch";
import classes from "./ad-copies.module.css";
import { countsOf } from "./elements";
import { ReferencePicture } from "./ReferencePicture";

export interface TabCopyListProps {
  teamId: string;
  brandId: string;
  copies: readonly AdCopy[];
  loading: boolean;
  /** The products' names by id, for the rows' badges. */
  productNames: ReadonlyMap<string, string>;
  search: string;
  onSearch: (search: string) => void;
  productId: string | null;
  onProduct: (productId: string | null) => void;
  chosenId: string | null;
  onChoose: (copy: AdCopy) => void;
  /** A duplicate or a delete runs: the menus wait, as live's do. */
  busyId: string | null;
  onDuplicate: (copy: AdCopy) => void;
  onDelete: (copy: AdCopy) => void;
}

/**
 * The Ad copy tab's list (live's `text-copy-tab/copies-list`, captures advanced-234/01, 04, 08 and
 * 09): "Ad copies" with its fold, live's search and product filter, and a row per copy with its
 * number, title, product, sections and values, its menu (Duplicate, Delete) and its inspiration.
 */
export function TabCopyList(props: TabCopyListProps) {
  const { teamId, brandId, copies, loading, productNames, search, onSearch } = props;
  const { productId, onProduct, chosenId, onChoose, busyId, onDuplicate, onDelete } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [filtering, setFiltering] = useState(false);
  return (
    <div className={classes.tabList} data-collapsed={collapsed || undefined}>
      <div className={classes.listHeader}>
        {collapsed ? null : <Text className={classes.listTitle}>Ad copies</Text>}
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label={collapsed ? "Open the Ad copies" : "Fold the Ad copies"}
          onClick={() => setCollapsed((current) => !current)}
        >
          {collapsed ? (
            <PanelLeftOpen size={20} aria-hidden />
          ) : (
            <PanelLeftClose size={20} aria-hidden />
          )}
        </ActionIcon>
      </div>
      {collapsed ? null : (
        <div className={classes.tabTools}>
          <SubmitSearch
            label="Search by title"
            value={search}
            onSearch={onSearch}
            className={classes.search}
          />
          <ProductListPopover
            teamId={teamId}
            brandId={brandId}
            chosenId={productId}
            opened={filtering}
            onClose={() => setFiltering(false)}
            // Live's filter: a pick of the product chosen clears it.
            onPick={(product) =>
              onProduct(product.productId === productId ? null : product.productId)
            }
            closeOnPick={false}
            position="bottom-start"
            anchor={(wrap) => (
              <span className={classes.filter}>
                {wrap(
                  <ActionIcon
                    size={36}
                    variant={productId === null && !filtering ? "default" : "light"}
                    aria-label="Product"
                    aria-expanded={filtering}
                    onClick={() => setFiltering((open) => !open)}
                  >
                    <Archive size={20} strokeWidth={1.5} aria-hidden />
                  </ActionIcon>,
                )}
                {productId === null ? null : (
                  <ActionIcon
                    variant="default"
                    size="xs"
                    radius="xl"
                    className={classes.filterClear}
                    aria-label="Clear the product"
                    onClick={() => onProduct(null)}
                  >
                    <X size={10} aria-hidden />
                  </ActionIcon>
                )}
              </span>
            )}
          />
        </div>
      )}
      {loading ? <Loader size="sm" aria-label="Loading the Ad copies" /> : null}
      {!loading && copies.length === 0 && !collapsed ? (
        <Text className={classes.empty}>No Ad copy found</Text>
      ) : null}
      <div className={classes.rows}>
        {copies.map((copy, at) => (
          <Row
            key={copy.id}
            teamId={teamId}
            brandId={brandId}
            copy={copy}
            number={at + 1}
            product={productNames.get(copy.product_id) ?? ""}
            chosen={copy.id === chosenId}
            collapsed={collapsed}
            busy={busyId !== null}
            onChoose={() => onChoose(copy)}
            onDuplicate={() => onDuplicate(copy)}
            onDelete={() => onDelete(copy)}
          />
        ))}
      </div>
    </div>
  );
}

interface RowProps {
  teamId: string;
  brandId: string;
  copy: AdCopy;
  number: number;
  product: string;
  chosen: boolean;
  collapsed: boolean;
  busy: boolean;
  onChoose: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

/** A copy's row (live's `text-copy-item`): a click chooses it; its menu opens on a click or hover. */
function Row(props: RowProps) {
  const { teamId, brandId, copy, number, product, chosen, collapsed, busy } = props;
  const writing = copy.status === "in_progress";
  const { sections, elements } = countsOf(copy.text_elements);
  return (
    <Tooltip label={`${copy.name}, ${sections}`} position="right" withArrow disabled={!collapsed}>
      <div className={classes.tabRow} data-current={chosen || undefined}>
        <UnstyledButton
          className={classes.tabRowChoose}
          aria-current={chosen}
          aria-label={copy.name}
          onClick={props.onChoose}
        >
          <span className={classes.number}>{number}</span>
          {collapsed ? null : (
            <span className={classes.tabRowText}>
              <span className={classes.rowTitle}>{copy.name}</span>
              <span className={classes.badges}>
                <Tooltip label={product} disabled={product === ""}>
                  <span className={classes.badge}>{product}</span>
                </Tooltip>
                <Tooltip label={`Sections: ${sections}`}>
                  <span className={classes.badge}>
                    <LayoutGrid size={16} aria-hidden />
                    {writing ? <Loader size={10} aria-label="Generating" /> : sections}
                  </span>
                </Tooltip>
                <Tooltip label={`Elements: ${elements}`}>
                  <span className={classes.badge}>
                    <SquareStack size={16} aria-hidden />
                    {writing ? <Loader size={10} aria-hidden /> : elements}
                  </span>
                </Tooltip>
              </span>
            </span>
          )}
        </UnstyledButton>
        {collapsed ? null : (
          <>
            <Menu trigger="click-hover" position="bottom-start" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" aria-label={`Actions for ${copy.name}`}>
                  <EllipsisVertical size={16} aria-hidden />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<Copy size={20} strokeWidth={1.5} />}
                  disabled={busy}
                  onClick={props.onDuplicate}
                >
                  Duplicate
                </Menu.Item>
                <Menu.Item
                  leftSection={<Trash size={20} strokeWidth={1.5} />}
                  disabled={busy}
                  onClick={props.onDelete}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <ReferencePicture teamId={teamId} brandId={brandId} reference={copy.reference} />
          </>
        )}
      </div>
    </Tooltip>
  );
}
